import { DocumentsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { NotificationService } from '@shared';
import { from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as DocumentsActions from './documents.actions';

@Injectable()
export class DocumentsEffects {
  private actions$ = inject(Actions);
  private documentsApi = inject(DocumentsApiService);
  private notification = inject(NotificationService);

  loadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.loadDocuments),
      switchMap(({ params }) =>
        this.documentsApi.findAll(params).pipe(
          map(response => DocumentsActions.loadDocumentsSuccess({
            documents: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_documents_error');
            return of(DocumentsActions.loadDocumentsFailure({ error: error.message || 'Failed to load documents' }));
          }),
        ),
      ),
    ),
  );

  createDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.createDocument),
      switchMap(({ documentData }) =>
        this.documentsApi.create(documentData).pipe(
          map((document) => {
            this.notification.success('admin.notifications.document_created');
            return DocumentsActions.createDocumentSuccess({ document });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_document_error');
            return of(DocumentsActions.createDocumentFailure({ error: error.message || 'Failed to create document' }));
          }),
        ),
      ),
    ),
  );

  updateDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.updateDocument),
      switchMap(({ id, documentData, fileSk, fileEn }) =>
        this.documentsApi.update(id, documentData).pipe(
          switchMap((document) => {
            this.notification.success('admin.notifications.document_updated');
            const followUpActions: Action[] = [
              DocumentsActions.updateDocumentSuccess({ document }),
            ];

            if (fileSk) {
              followUpActions.push(DocumentsActions.uploadDocumentFile({ id, file: fileSk, lang: 'sk' }));
            }
            if (fileEn) {
              followUpActions.push(DocumentsActions.uploadDocumentFile({ id, file: fileEn, lang: 'en' }));
            }

            return from(followUpActions);
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_document_error');
            return of(DocumentsActions.updateDocumentFailure({ error: error.message || 'Failed to update document' }));
          }),
        ),
      ),
    ),
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocument),
      switchMap(({ id }) =>
        this.documentsApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.document_deleted');
            return DocumentsActions.deleteDocumentSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_document_error');
            return of(DocumentsActions.deleteDocumentFailure({ error: error.message || 'Failed to delete document' }));
          }),
        ),
      ),
    ),
  );

  uploadDocumentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.uploadDocumentFile),
      switchMap(({ id, file, lang }) =>
        this.documentsApi.uploadFile(id, file, lang).pipe(
          map((document) => {
            this.notification.success('admin.notifications.document_file_uploaded');
            return DocumentsActions.uploadDocumentFileSuccess({ document });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.upload_document_file_error');
            return of(DocumentsActions.uploadDocumentFileFailure({ error: error.message || 'Failed to upload document file' }));
          }),
        ),
      ),
    ),
  );

  deleteDocumentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocumentFile),
      switchMap(({ id, lang }) =>
        this.documentsApi.deleteFile(id, lang).pipe(
          map((document) => {
            this.notification.success('admin.notifications.document_file_deleted');
            return DocumentsActions.deleteDocumentFileSuccess({ document });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_document_file_error');
            return of(DocumentsActions.deleteDocumentFileFailure({ error: error.message || 'Failed to delete document file' }));
          }),
        ),
      ),
    ),
  );
}
